/**
 * Constants for the Chatbot module
 */

import { palette } from '@/styles/design-tokens';

/** Mirrors --grey-white / --color-bg-white (not yet in palette JS) */
const SURFACE_WHITE = '#FFFFFF';

/** WebChat avatarSize — keep in sync with --size-avatar */
export const BOT_AVATAR_SIZE_PX = 40;
/** AskNoa inset — keep in sync with --space-5 */
export const BOT_AVATAR_IMAGE_PADDING_PX = 10;
/** Bubble / suggested-action radius — keep in sync with --radius-10 */
export const BUBBLE_BORDER_RADIUS_PX = 10;

export const WEBCHAT_STYLE_OPTIONS = {
  backgroundColor: palette.gray050, // --grey-50
  bubbleBackground: SURFACE_WHITE, // --color-bg-white
  bubbleBorderColor: palette.menuHoverBg, // --grey-100 / --color-border-light
  bubbleBorderRadius: BUBBLE_BORDER_RADIUS_PX,
  bubbleFromUserBackground: palette.primary, // --color-primary-base
  bubbleFromUserBorderColor: palette.primary,
  bubbleFromUserBorderRadius: BUBBLE_BORDER_RADIUS_PX,
  bubbleFromUserTextColor: SURFACE_WHITE,
  avatarSize: BOT_AVATAR_SIZE_PX,
  avatarBorderRadius: '50%', // --radius-circle
  botAvatarBackgroundColor: SURFACE_WHITE,
  userAvatarBackgroundColor: palette.primary,
  hideScrollToEndButton: false,
  accent: palette.primary,
  sendBoxBackground: SURFACE_WHITE,
  sendBoxBorderTop: `1px solid ${palette.menuHoverBg}`,
  suggestedActionBackground: SURFACE_WHITE,
  suggestedActionBorderColor: palette.primary,
  suggestedActionTextColor: palette.primary,
  suggestedActionBorderRadius: `${BUBBLE_BORDER_RADIUS_PX}px`,
  timestampColor: palette.gray500, // --grey-500
  transcriptOverlayButtonBackground: 'rgba(107, 103, 206, 0.8)', // --color-caccounting-rgb @ 80%
  transcriptOverlayButtonColor: SURFACE_WHITE,
  primaryFont: '"Roboto", system-ui, -apple-system, "Segoe UI", sans-serif', // --font-family-base
  sendBoxPlaceholderColor: palette.gray500,
  sendBoxTextColor: palette.gray700, // --grey-700
  botAvatarImage: '/img/navicon/fbc-nav/AskNoa.svg',
  botAvatarInitials: '',
  userAvatarImage: '',
  userAvatarInitials: 'ME',
};

export const CONNECTION_STATUSES = {
  CONNECTED: 2,
  FAILED: 4
};

export const LOCALIZED_STRINGS = {
  TEXT_INPUT_PLACEHOLDER: "Type your message here..."
};

/**
 * Direct Line API endpoint for token generation
 * Uses environment variable if available, otherwise falls back to default
 */
export const DIRECT_LINE_TOKEN_URL = process.env.VUE_APP_DIRECT_LINE_TOKEN_URL || 'https://directline.botframework.com/v3/directline/tokens/generate';

/**
 * UI Text constants
 */
export const UI_TEXT = {
  LOADING_CHAT: 'Loading chat...',
  RETRY_BUTTON: 'Retry',
  COMPONENT_NAME: 'Chatbot',
  RIGHT_PANEL: 'ArkAgent',
  WELCOME_TITLE: 'Hi, how are you doing today?',
  WELCOME_SUBTITLE: 'Ask me anything about your finance close data, reports, or analysis.',
  WELCOME_INPUT_PLACEHOLDER: 'Type your message here...'
};

/**
 * Error messages
 */
export const ERROR_MESSAGES = {
  FETCH_TOKEN_FAILED: 'Failed to fetch chat token. Please try again later.',
  INITIALIZE_CHAT_ERROR: 'Error initializing chat. Please try again later.',
  CONNECTION_FAILED: 'Connection to chat service failed. Please try again later.',
  CONNECTION_ERROR_PREFIX: 'Error connecting to chat service: ',
  INITIALIZE_WEBCHAT_ERROR: 'Failed to initialize chat: ',
  WEBCHAT_NOT_LOADED: 'WebChat library not loaded. Please try again.'
};

/**
 * Console log messages
 */
export const CONSOLE_MESSAGES = {
  DIRECTLINE_CONNECTION_STATUS: 'DirectLine connection status:',
  DIRECTLINE_CONNECTION_ERROR: 'DirectLine connection error:',
  INITIALIZE_WEBCHAT_ERROR: 'Error initializing WebChat:',
  FETCH_TOKEN_ERROR: 'Error fetching token:',
  WEBCHAT_NOT_INITIALIZED: 'WebChat or DirectLine not initialized',
  SESSION_EXPIRED: '[ChatBot]: Session expired, redirecting to login',
  LOGOUT_ERROR: '[ChatBot]: Logout error:',
  DECRYPT_TOKEN_DEBUG: '[ChatbotStore]: Direct Line token decrypt debug',
  DECRYPT_TOKEN_ERROR: '[ChatbotStore]: Error decrypting Direct Line token',
  TOKEN_HEADER_INVALID: '[ChatbotStore]: Decrypted token is not a valid HTTP header value',
  TOKEN_EXCHANGE_DEBUG: '[ChatbotStore]: Direct Line token exchange debug',
  TOKEN_EXCHANGE_WARN: '[ChatbotStore]: Token generation failed, using original'
};

/**
 * Default fallback encryption key name (value never logged in full)
 */
export const JWT_SECRET_FALLBACK = 'consark-butterfly-secure-key';

/**
 * DOM selectors and class names
 */
export const DOM_SELECTORS = {
  WEBCHAT_CONTAINER: 'webchat',
  WEBCHAT_INPUT: '.webchat__send-box-text-box__input',
  SEND_BOX_MAIN: '.webchat__send-box__main',
  SEND_BUTTON: '.webchat__send-button',
  SUGGESTIONS_BUTTON: '.suggestions-button'
};

/**
 * DOM attributes and styles
 */
export const DOM_ATTRIBUTES = {
  WEBCHAT_HEIGHT: '100%',
  WEBCHAT_WIDTH: '100%',
  SUGGESTIONS_BUTTON_CLASS: 'suggestions-button',
  INPUT_EVENT_TYPE: 'input',
  INPUT_EVENT_BUBBLES: true
};

/**
 * WebChat configuration
 */
export const WEBCHAT_CONFIG = {
  LOCALE: 'en-US'
};

/**
 * Mutation Observer configuration
 */
export const OBSERVER_CONFIG = {
  CHILD_LIST: true,
  SUBTREE: true
};

/**
 * ChatSuggestions component constants
 */
export const CHAT_SUGGESTIONS_UI = {
  COMPONENT_NAME: 'ChatSuggestions',
  HEADER_TITLE: 'SUGGESTED PROMPTS',
  CLOSE_BUTTON: '✕',
  NO_SUGGESTIONS_MESSAGE: 'No suggestions available at the moment.'
};

/**
 * Application type aliases and mappings
 */
export const FLUX_ALIASES = ['flux', 'variance', 'varianceanalysis', 'va'];
export const FBC_ALIASES = ['fbc', 'financialclose', 'financial_close', 'financial close'];

/**
 * User role definitions for financial close operations
 */
export const ROLE_LIST = [
  { id: 'manager', name: 'Manager' },
  { id: 'preparer', name: 'Preparer' },
  { id: 'approver', name: 'Approver' },
];

/**
 * API endpoints
 */
export const API_ENDPOINTS = {
  LOGOUT: '/v2/auth/logout',
  TOOLS_CATALOG: '/v2/chatbot/toolscatalog'
};

/**
 * Route paths
 */
export const ROUTE_PATHS = {
  HOME: '/'
};

/**
 * Success messages
 */
export const SUCCESS_MESSAGES = {
  LOGOUT_SUCCESS: 'Logged out successfully'
};

/**
 * Delay timings (in milliseconds)
 */
export const TIMINGS = {
  SESSION_EXPIRED_DELAY: 2000
};

/**
 * Error codes
 */
export const ERROR_CODES = {
  WIDGET_TOKEN_EXPIRED: 'WIDGET_TOKEN_EXPIRED'
};
